import { useQuery } from "@apollo/client";
import { withJsonFormsMultiEnumProps } from "@jsonforms/react";
import { Modal } from "pepsico-ds";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { listSegments } from "../../../graphql/queries/listSegments";
import ButtonGroupDynamic from "../../common/ButtonGroupDynamic";
import ToastNotification from "../../common/toastNotification/ToastNotification";
import SegmentSelect from "./SegmentSelect";
import "./_campaign-segment-select.scss";

const CampaignSegmentSelectComponent = (props) => {
  const { handleChange, path, data, uischema } = props;
  const isMultipleSelection = uischema.options?.multiple !== false;

  const [openModal, setOpenModal] = useState(false);
  const [segments, setSegments] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [savedSegments, setSavedSegments] = useState([]);
  const [hasFetchedSegments, setHasFetchedSegments] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: "",
    title: "",
    description: "",
  });
  useQuery(listSegments, {
    variables: {
      filter: {
        status: "ENABLED",
      },
    },
    onCompleted: (segmentsData) => {
      setSegments(segmentsData.listSegments);
      setHasFetchedSegments(true);
    },
  });

  useEffect(() => {
    if (!hasFetchedSegments || !data) return;

    const dataArray = isMultipleSelection
      ? data
      : typeof data === "string"
        ? [data]
        : [];

    const newSelectedSegments = dataArray.map((item) => {
      const segment = segments.find((segment) => segment.id === item);
      return (
        segment || {
          id: item,
          name: "Selected segment is no longer active",
          status: "DISABLED",
        }
      );
    });

    setSelectedSegments((prev) => {
      const prevIds = prev.map((seg) => seg.id);
      const newIds = newSelectedSegments.map((seg) => seg.id);
      if (
        prevIds.length !== newIds.length ||
        !newIds.every((id, idx) => id === prevIds[idx])
      ) {
        return newSelectedSegments;
      }
      return prev;
    });

    setSavedSegments((prev) => {
      const prevIds = prev.map((seg) => seg.id);
      const newIds = newSelectedSegments.map((seg) => seg.id);
      if (
        prevIds.length !== newIds.length ||
        !newIds.every((id, idx) => id === prevIds[idx])
      ) {
        return newSelectedSegments;
      }
      return prev;
    });
  }, [hasFetchedSegments, data, segments, isMultipleSelection]);

  useEffect(() => {
    const segmentIds = savedSegments.map((segment) => segment.id);
    const value = isMultipleSelection
      ? segmentIds
      : segmentIds.length > 0
        ? segmentIds[0]
        : "";

    handleChange(path, value);
  }, [savedSegments, handleChange, path, isMultipleSelection]);

  const handleToggleModal = () => setOpenModal(!openModal);
  const handleCloseModal = () => {
    setSelectedSegments(savedSegments);
    handleToggleModal();
  };
  const handleSegmentSelection = (selected) => setSelectedSegments(selected);
  const handleSaveSegments = () => {
    const newSegments = selectedSegments.filter(
      (segment) => !savedSegments.some((saved) => saved.id === segment.id)
    );
    setSavedSegments(selectedSegments);
    handleToggleModal();
    openSuccessToast(newSegments);
  };
  const handleRemoveSaved = (id) => {
    const filteredSegments = savedSegments.filter((segment) => segment.id !== id);
    setSelectedSegments(filteredSegments);
    setSavedSegments(filteredSegments);
  };
  const openSuccessToast = (newSegments) => {
    const formatNames = (names) => {
      if (names.length === 0) return "";
      if (names.length === 1) return names[0];

      const lastName = names.pop();
      return `${names.join(", ")} and ${lastName}`;
    };

    const newSelectedSegments = formatNames(
      newSegments.map((segment) => segment.name)
    );
    setToast({
      open: true,
      type: "success",
      title: `${newSelectedSegments} segment added`,
      description: isMultipleSelection
        ? "You have just added segments!"
        : "You have just updated the segment selection!",
    });
  };

  return (
    <div className="segment-select">
      {savedSegments.map((segment) => (
        <div
          className={`segment-item display-flex flex-direction-row font-xs font-bold segment-added ${
            segment.status === "DISABLED" ? "segment-disabled" : ""
          }`}
          key={segment.id}
        >
          <div className="added-segment display-flex flex-direction-row align-items-center justify-content-space-between">
            <span>{segment.name}</span>
            <ButtonGroupDynamic
              size="small"
              items={[
                {
                  icon: "delete",
                  onClick: () => handleRemoveSaved(segment.id),
                },
              ]}
            />
          </div>
        </div>
      ))}
      <div className="segment-item display-flex flex-direction-row font-xs font-bold">
        <ButtonGroupDynamic
          size="small"
          items={[
            {
              icon: "add",
              onClick: handleToggleModal,
            },
          ]}
        />
        Add New Segment
      </div>
      <ToastNotification
        {...toast}
        handleClose={() => setToast({ ...toast, open: false })}
      />
      <Modal
        className="campaign-segment-select-modal"
        showModal={openModal}
        title="Choose Segment"
        text=""
        showSecondaryButton={false}
        showTertiaryButton={false}
        showLink={false}
        onCloseModal={handleCloseModal}
        primaryButtonProps={{
          text: "Save",
          disabled: !selectedSegments.length,
          onClick: handleSaveSegments,
        }}
      >
        <SegmentSelect
          segmentsData={segments}
          selected={selectedSegments}
          onChange={handleSegmentSelection}
          selection={isMultipleSelection ? "multiple" : "single"}
        />
      </Modal>
    </div>
  );
};

CampaignSegmentSelectComponent.propTypes = {
  handleChange: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  data: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
    .isRequired,
  uischema: PropTypes.shape({
    options: PropTypes.shape({
      multiple: PropTypes.bool,
    }),
  }),
};

export const CampaignSegmentSelectControl = withJsonFormsMultiEnumProps(
  CampaignSegmentSelectComponent
);
